import Image from "next/image";
import axios from "axios";
import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import { useInView } from 'react-intersection-observer';
import { ProductReviewItemProps } from "./constants";
import Lightbox from "yet-another-react-lightbox";
import NextJsImage from "../Utils/NextJsImage";
import Loading from "../Common/Loading";
import "yet-another-react-lightbox/styles.css";


const ReviewList = () => {
  const router = useRouter();
  const [page, setPage] = useState(1);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewCount, setReviewCount] = useState(0);
  const [reviewList, setReviewList] = useState<Array<ProductReviewItemProps>>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState(true);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [images, setImages] = useState<Array<{ src: string }>>([]);
  const { ref, inView, entry } = useInView({
    /* Optional options */
    threshold: 0,
  });

  const handleClick = (review: ProductReviewItemProps) => {
    setImages(review.image_list.map((image) => ({ src: image.image })));
    setGalleryOpen(true);
  }

  const handleLoadMore = useCallback(async () => {
    setLoading(true);

    try {
      const response = await axios.get(
        "/api/review",
        { params: { slug: router.query.slug, start: (page - 1) * 8, end: page * 8 } }
      )

      if (response.status === 200) {
        setReviewList([...reviewList, ...response.data.review_list]);
        setReviewCount(response.data.counts);
        setReviewRating(response.data.review_rating);
        setPage(page + 1)

        if (response.data.review_list.length < 8) {
          setQuery(false);
        }
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }, [page, reviewList, router.query.slug]);

  useEffect(() => {
    const handleRouteChange = async () => {
      await handleLoadMore();
    }

    router.events.on('routeChangeComplete', handleRouteChange)
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    };
  })

  useEffect(() => {
    const fetchData = async () => {
      await handleLoadMore();
    };

    if (inView && !loading && query) {
      fetchData();
    }
  }, [handleLoadMore, inView, loading, query]);

  return (
    <div className="text-sm text-black font-poppins pb-10">
      <div className="md:hidden">
        <div
          className="container flex items-center py-6 space-x-4 border-b border-solid md:hidden border-reviewBg"
          onClick={() => router.back()}
        >
          <Image
            src={process.env.NEXT_PUBLIC_STATIC_SITE + "/images/icons/arrow-down.png"}
            alt="arrow icon"
            width={12}
            height={12}
            quality={100}
            className={`transition-transform ease-in-out duration-300 transform rotate-90`}
          />
          <p className="text-xs leading-5 text-black">Produk Review</p>
        </div>
        <div className="mt-6 container">
          <h1 className="text-base font-bold">Ulasan Produk</h1>
          <div className="mt-4 flex space-x-2 items-center">
            <p className="text-[32px] font-medium">{reviewRating.toFixed(1)}/5</p>
            <div className="flex">
              {Array.from({ length: 5 }, (_, index) => (
                <Image
                  key={index}
                  src={
                    reviewRating >= index + 1
                      ? process.env.NEXT_PUBLIC_STATIC_SITE + "/images/icons/star.png"
                      : reviewRating >= index + 0.5
                      ? process.env.NEXT_PUBLIC_STATIC_SITE + "/images/icons/half-star.png"
                      : process.env.NEXT_PUBLIC_STATIC_SITE + "/images/icons/empty-star.png"
                  }
                  alt="star icon"
                  width={16}
                  height={16}
                  quality={100}
                  className="mr-1"
                />
              ))}
            </div>
            <p className="">({reviewCount} Reviews)</p>
          </div>
        </div>
        <hr className="my-6 bg-reviewBg h-1" />
        {reviewList.map((review, index) => (
          <div key={index}>
            {index > 0 && <hr className="my-6 bg-reviewBg" />}
            <div className="container">
              <p className="text-base font-medium text-productReview">{review.customer_name}</p>
              <div className="flex justify-between items-center mt-2">
                <div className="flex">
                  {Array.from({ length: 5 }, (_, index) => (
                    <Image
                      key={index}
                      src={
                        review.star_value >= index + 1
                          ? process.env.NEXT_PUBLIC_STATIC_SITE + "/images/icons/star.png"
                          : reviewRating >= index + 0.5
                          ? process.env.NEXT_PUBLIC_STATIC_SITE + "/images/icons/half-star.png"
                          : process.env.NEXT_PUBLIC_STATIC_SITE + "/images/icons/empty-star.png"
                      }
                      alt="star icon"
                      width={16}
                      height={16}
                      quality={100}
                      className="mr-1"
                    />
                  ))}
                </div>
                <p className="text-sm text-productReview">{review.review_date}</p>
              </div>
              <p className="text-base font-bold mt-4">{review.title}</p>
              <p className="mt-4 text-productReview" dangerouslySetInnerHTML={{__html: review.content.replace(/(?:\r\n|\r|\n)/g, "<br />")}}></p>
              {review.image_list.length > 0 && (
                <div className="flex space-x-4 flex-wrap mt-4">
                  {review.image_list.map((image, index) => (
                    <Image
                      key={index}
                      src={image.image}
                      alt="review image"
                      width={80}
                      height={80}
                      quality={100}
                      className="!object-cover h-[135px] mr-4 mb-4 cursor-pointer"
                      onClick={() => handleClick(review)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="hidden md:block container mt-10">
        <div className="flex items-center space-x-4 p-8 border border-reviewBg border-solid rounded-lg mb-8">
          <h1 className="text-lg font-bold">Rating Produk</h1>
          <div className="flex space-x-2 items-center">
            <div className="flex">
              {Array.from({ length: 5 }, (_, index) => (
                <Image
                  key={index}
                  src={
                    reviewRating >= index + 1
                      ? process.env.NEXT_PUBLIC_STATIC_SITE + "/images/icons/star.png"
                      : reviewRating >= index + 0.5
                      ? process.env.NEXT_PUBLIC_STATIC_SITE + "/images/icons/half-star.png"
                      : process.env.NEXT_PUBLIC_STATIC_SITE + "/images/icons/empty-star.png"
                  }
                  alt="star icon"
                  width={16}
                  height={16}
                  quality={100}
                  className="mr-1"
                />
              ))}
            </div>
            <p className="text-mooimom">({reviewCount} Reviews)</p>
          </div>
        </div>
        <div className="space-y-4">
          {reviewList.map((review, index) => (
            <div key={index} className="p-4 border border-reviewBg border-solid rounded-lg">
              <p className="text-base font-medium text-productReview">{review.customer_name}</p>
              <div className="flex justify-between items-center mt-2">
                <div className="flex">
                  {Array.from({ length: 5 }, (_, index) => (
                    <Image
                      key={index}
                      src={
                        review.star_value >= index + 1
                          ? process.env.NEXT_PUBLIC_STATIC_SITE + "/images/icons/star.png"
                          : reviewRating >= index + 0.5
                          ? process.env.NEXT_PUBLIC_STATIC_SITE + "/images/icons/half-star.png"
                          : process.env.NEXT_PUBLIC_STATIC_SITE + "/images/icons/empty-star.png"
                      }
                      alt="star icon"
                      width={16}
                      height={16}
                      quality={100}
                      className="mr-1"
                    />
                  ))}
                </div>
                <p className="text-sm text-productReview">{review.review_date}</p>
              </div>
              <p className="text-base font-bold mt-4">{review.title}</p>
              <p className="mt-4 text-productReview" dangerouslySetInnerHTML={{__html: review.content.replace(/(?:\r\n|\r|\n)/g, "<br />")}}></p>
              {review.image_list.length > 0 && (
                <div className="flex space-x-4 flex-wrap mt-4">
                  {review.image_list.map((image, index) => (
                    <Image
                      key={index}
                      src={image.image}
                      alt="review image"
                      width={80}
                      height={80}
                      quality={100}
                      className="!object-cover h-[135px] mr-4 mb-4 cursor-pointer"
                      onClick={() => handleClick(review)}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <div ref={ref}></div>
      <Lightbox
        open={galleryOpen}
        close={() => setGalleryOpen(false)}
        styles={{ root: { "--yarl__color_backdrop": "rgba(0, 0, 0, .7)" } }}
        controller={{
          closeOnPullUp: true,
          closeOnPullDown: true,
          closeOnBackdropClick: true,
        }}
        slides={images}
        render={{
          slide: NextJsImage,
          buttonPrev: images.length > 1 ? undefined : () => null,
          buttonNext: images.length > 1 ? undefined : () => null,
        }}
      />
    </div>
  )
}

export default ReviewList
